import { DateTime } from 'luxon';
import { validateLaunchDateTimeRule } from '@/lib/campaign-fields';

/**
 * "Quando enviar" of the campaign composer. Every rule runs in the timezone the
 * merchant chose (never the browser's) and receives `now` as a parameter.
 */
export type ScheduleChoice =
  | { kind: 'now' }
  | { kind: 'todayEvening' } // hoje 19:00 no fuso escolhido
  | { kind: 'tomorrowMorning' } // amanhã 10:00 no fuso escolhido
  | {
      kind: 'custom';
      date: string /* yyyy-MM-dd */;
      time: string /* HH:mm ou '' */;
    };

export const EVENING_HOUR = 19,
  MORNING_HOUR = 10,
  EVENING_MIN_LEAD_MINUTES = 15,
  MAX_MONTHS_AHEAD = 2;

/**
 * Cadência do agendador de campanhas em prod (cron do backend,
 * APP__CRONJOBS__CAMPAIGN_SCHEDULER_CRON). Único lugar a mudar se prod mudar;
 * a confirmação do "Agora" diz "Sai em até {n} minuto(s)".
 */
export const CAMPAIGN_SCHEDULER_INTERVAL_MINUTES = 1;

const DATE_FORMAT = 'yyyy-MM-dd';
const TIME_PATTERN = /^(\d{2}):(\d{2})$/;

function todayAt(now: DateTime, zone: string, hour: number): DateTime {
  return now
    .setZone(zone)
    .startOf('day')
    .set({ hour, minute: 0, second: 0, millisecond: 0 });
}

/** now(zone) <= hoje 19:00 - 15 min (limite incluso). */
export function isTodayEveningAvailable(now: DateTime, zone: string): boolean {
  const lastMoment = todayAt(now, zone, EVENING_HOUR).minus({
    minutes: EVENING_MIN_LEAD_MINUTES,
  });
  return now <= lastMoment;
}

type CustomResolution =
  | { kind: 'ok'; launch: DateTime }
  | { kind: 'missing' | 'timeMissing' | 'nonexistentTime' };

function resolveCustom(
  date: string,
  time: string,
  zone: string
): CustomResolution {
  const day = DateTime.fromFormat(date, DATE_FORMAT, { zone });
  if (!day.isValid) return { kind: 'missing' };
  if (time === '') return { kind: 'timeMissing' };

  const match = TIME_PATTERN.exec(time);
  if (!match) return { kind: 'timeMissing' };
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return { kind: 'timeMissing' };

  const launch = DateTime.fromObject(
    { year: day.year, month: day.month, day: day.day, hour, minute },
    { zone }
  );
  // Luxon moves a wall time that does not exist (daylight-saving gap) forward.
  if (!launch.isValid || launch.hour !== hour || launch.minute !== minute) {
    return { kind: 'nonexistentTime' };
  }
  return { kind: 'ok', launch };
}

/**
 * now -> now.setZone(zone).startOf('minute').plus({ minutes: 1 }); custom sem
 * hora / inválido -> null; horário inexistente no fuso (lacuna de horário de
 * verão) -> null.
 */
export function resolveLaunch(
  choice: ScheduleChoice,
  now: DateTime,
  zone: string
): DateTime | null {
  switch (choice.kind) {
    case 'now':
      return now.setZone(zone).startOf('minute').plus({ minutes: 1 });
    case 'todayEvening':
      return todayAt(now, zone, EVENING_HOUR);
    case 'tomorrowMorning': {
      const tomorrow = now.setZone(zone).startOf('day').plus({ days: 1 });
      return DateTime.fromObject(
        {
          year: tomorrow.year,
          month: tomorrow.month,
          day: tomorrow.day,
          hour: MORNING_HOUR,
        },
        { zone }
      );
    }
    case 'custom': {
      const resolution = resolveCustom(choice.date, choice.time, zone);
      return resolution.kind === 'ok' ? resolution.launch : null;
    }
  }
}

export type ScheduleIssue =
  | 'missing'
  | 'timeMissing'
  | 'nonexistentTime'
  | 'dateInPast'
  | 'dateTooFar'
  | 'timeInPast';

/**
 * Usa validateLaunchDateTimeRule(resolveLaunch(...).toISO(), now) — fuso
 * escolhido em tudo; 'todayEvening' indisponível -> 'timeInPast'.
 */
export function validateSchedule(
  choice: ScheduleChoice | null,
  now: DateTime,
  zone: string
): ScheduleIssue | null {
  if (choice === null) return 'missing';
  if (choice.kind === 'todayEvening' && !isTodayEveningAvailable(now, zone)) {
    return 'timeInPast';
  }
  if (choice.kind === 'custom') {
    const resolution = resolveCustom(choice.date, choice.time, zone);
    if (resolution.kind !== 'ok') return resolution.kind;
  }

  const launch = resolveLaunch(choice, now, zone);
  const iso = launch?.toISO();
  if (!iso) return 'missing';

  const code = validateLaunchDateTimeRule(iso, now);
  if (code === 'required' || code === 'dateInvalid') return 'missing';
  return code;
}

export type PrimaryAction =
  | { kind: 'sendNow' }
  | {
      kind: 'schedule';
      day: 'today' | 'tomorrow' | 'date';
      dateLabel: string /* "7 nov" */;
      time: string /* "19:00" */;
    }
  | { kind: 'scheduleIncomplete' };

/** "7 nov" (pt) / "Nov 7" (en). */
function formatDayLabel(day: DateTime, locale: string): string {
  if (locale.toLowerCase().startsWith('pt')) {
    return day.setLocale('pt-BR').toFormat('d LLL').replace(/\.$/, '');
  }
  return day.setLocale('en-US').toFormat('LLL d');
}

export function getPrimaryAction(
  choice: ScheduleChoice | null,
  now: DateTime,
  zone: string,
  locale: string
): PrimaryAction {
  if (choice === null) return { kind: 'scheduleIncomplete' };
  if (choice.kind === 'now') return { kind: 'sendNow' };

  const launch = resolveLaunch(choice, now, zone);
  if (launch === null) return { kind: 'scheduleIncomplete' };

  return {
    kind: 'schedule',
    day:
      choice.kind === 'todayEvening'
        ? 'today'
        : choice.kind === 'tomorrowMorning'
          ? 'tomorrow'
          : 'date',
    dateLabel: formatDayLabel(launch, locale),
    time: launch.toFormat('HH:mm'),
  };
}

/** Hoje .. hoje + 2 meses (yyyy-MM-dd), no fuso escolhido. */
export function getCustomDateBounds(
  now: DateTime,
  zone: string
): { min: string; max: string } {
  const today = now.setZone(zone).startOf('day');
  return {
    min: today.toFormat(DATE_FORMAT),
    max: today.plus({ months: MAX_MONTHS_AHEAD }).toFormat(DATE_FORMAT),
  };
}

/**
 * `?date=` do calendário -> custom com a data clicada e time ''. Um ISO com
 * offset mantém o dia que o calendário mandou; sem offset, lê no fuso escolhido.
 */
export function parseDateParam(
  dateParam: string | null,
  zone: string
): ScheduleChoice | null {
  if (dateParam === null) return null;
  const day = DateTime.fromISO(dateParam, { zone, setZone: true });
  if (!day.isValid) return null;
  return { kind: 'custom', date: day.toFormat(DATE_FORMAT), time: '' };
}
